import React, { useEffect, useState } from 'react';
import { get } from 'aws-amplify/api';
import './AiPrediction.css';

// Reactのテキスト描画を使用し、本文のHTMLやスクリプトは実行しない。
function InlineText({ text }) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);
}

function heading(line) {
  const match = line.trim().match(/^(?:#{1,6}\s+|■\s*|【)(.*?)(?:】)?$/);
  return match ? match[1].replace(/\*\*/g, '').trim() : null;
}
const cells = line => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(s => s.trim());

function ReportBody({ body }) {
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const title = heading(line);
    if (title) { blocks.push(<h3 key={i}>{title}</h3>); continue; }
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1]) &&
        cells(lines[i + 1]).every(cell => /^:?-{3,}:?$/.test(cell))) {
      const headers = cells(line), rows = [];
      const key = i;
      i += 2;
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(cells(lines[i])); i++;
      }
      i--;
      blocks.push(<div className="ai-table-wrap" key={key}><table><thead><tr>{headers.map((cell, j) => <th key={j}><InlineText text={cell} /></th>)}</tr></thead><tbody>{rows.map((row, j) => <tr key={j}>{headers.map((_, k) => <td key={k}><InlineText text={row[k] || ''} /></td>)}</tr>)}</tbody></table></div>);
      continue;
    }
    blocks.push(<p key={i}><InlineText text={line} /></p>);
  }
  return blocks;
}

function PredictionReport({ report }) {
  // 印の章だけを先頭へ移動。メール本文の文章・印は変更しない。
  const lines = report.body.replace(/\r\n?/g, '\n').split('\n');
  const start = lines.findIndex(line => /^(?:現時点の)?(?:最終|暫定|予想)印/.test(heading(line) || ''));
  let markBody = '', remainder = report.body;
  if (start >= 0) {
    let end = start + 1;
    while (end < lines.length && !heading(lines[end])) end++;
    markBody = lines.slice(start, end).join('\n');
    remainder = [...lines.slice(0, start), ...lines.slice(end)].join('\n');
  }
  const stamp = new Date(report.generated_at);
  const date = report.generated_at && !Number.isNaN(stamp.getTime())
    ? new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(stamp) : null;
  const grade = report.confidence?.grade;
  return <>
    <div className="ai-report-meta">{date && <span>更新 {date}</span>}{['A', 'B', 'C', 'D'].includes(grade) && <span>◎信頼度 {grade}</span>}</div>
    <article className="ai-report" aria-label="AI予想本文">
      {markBody && <ReportBody body={markBody} />}
      <ReportBody body={remainder} />
    </article>
  </>;
}

export default function AiPrediction({ raceId }) {
  const [state, setState] = useState({ raceId: '', status: 'loading', report: null });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true, operation;
    setState({ raceId, status: 'loading', report: null });
    (async () => {
      try {
        operation = get({ apiName: 'readRaceCsvdev', path: '/getAiPrediction', options: { queryParams: { race_id: raceId } } });
        const { body } = await operation.response;
        const report = await body.json();
        if (report?.race_id !== raceId || report.phase !== 'final' || typeof report.body !== 'string' || !report.body.trim()) throw new Error('AI予想データが不正です');
        if (current) setState({ raceId, status: 'ready', report });
      } catch (error) {
        if (!current) return;
        const statusCode = error.response?.statusCode ?? error.response?.status;
        setState({ raceId, status: Number(statusCode) === 404 ? 'missing' : 'error', report: null });
      }
    })();
    return () => { current = false; operation?.cancel?.(); };
  }, [raceId, reload]);

  const status = state.raceId === raceId ? state.status : 'loading';
  return <section className="ai-prediction" aria-label="AI予想" aria-live="polite">
    {status === 'loading' && <p role="status">読み込み中...</p>}
    {status === 'missing' && <p className="ai-missing">AI予想生成前</p>}
    {status === 'error' && <div role="alert"><p>AI予想を取得できませんでした。</p><button type="button" onClick={() => setReload(n => n + 1)}>再読み込み</button></div>}
    {status === 'ready' && <PredictionReport report={state.report} />}
  </section>;
}
